import $ from 'jquery'
import store_finance from '@/store/mods/finances/finance.js'

/**
 * État vierge de l'assistant posologie.
 * mode « jour » : dose × fréquence (freq 1, 2 ou 3 par jour) × durée ; freq === 0 tant que
 *   le médecin n'a pas cliqué un chip (le texte saisi à la main n'est alors jamais écrasé).
 * mode « chaque » : dose toutes les N heures / tous les N jours (chaque, unite_chaque),
 *   à partir d'aujourd'hui, de demain ou d'une date (debut, debut_date), pendant la durée.
 */
function posoVide() {
    return { dose: 1, freq: 0, duree: 3, mode: 'jour', chaque: 6, unite_chaque: 'heures', debut: 'aujourdhui', debut_date: '' }
}

/**
 * Ligne de prescription vierge, avec l'état de l'assistant posologie.
 */
function lignePrescriptionVide() {
    return {
        medicament_stock: null,
        medicament_libelle: "",
        interne: false,
        quantite: 1,
        posologie: "",
        conditionnement: "",
        prix: 0,
        quantite_stock: null,
        _poso: posoVide()
    }
}

export default {
    name: 'ordonnance-medicale-modal',
    components: {},
    props: {
        consultation_id: Number,
        user_id: Number,
        evolution_id: Number,
        modal_toggler: Number,
        selected_ordonnance: Object,
        data_to_be_deleted: Object,
        selected_prescription: Object,
        info_patient: Object,
        info_caissier: Object,
        info_assurance_patient: {
            type: Object, 
            default: () => {}
        }
    },
    data() {
        return {
            save_loading: false,
            value: '',
            filtered_medicaments: [],
            prescriptions: [lignePrescriptionVide()],

            fields: ['categorie', 'libelle', 'qte_disponible',],
            filter: "",
            perPage: 10,
            currentPage: 1,

            is_error: false,
            permission_is_denied: false,
            del_loading: false,
            is_confirm: false,
            is_paid: false,
            patient_id: '',
            is_conflict: false,

            favoris: { internes: {}, externes: {} },
            posos: { internes: {}, externes: {} },
            modeles: [],
            nom_modele: '',
            modele_naming: false,
            represc: null,

            error_message: ''
        }
    },

    watch: {
        modal_toggler() {
            this.save_loading = false
            this.clearError()
            this.prescriptions = [lignePrescriptionVide()]
            this.loadFavoris()
            this.loadPosos()
            this.loadModeles()
            this.loadRepresc()
        }, 

        data_to_be_deleted() {
            this.clearError() 
        }
    },

    computed: {
        medicaments() {
            return this.$store.getters["medicaments"];
        },

        can_add_differential_computing() {
            return this.$store.getters["can_add_differential_computing"]
        },

        /**
         * Médicaments du stock les plus prescrits par ce médecin (top 8).
         */
        medicaments_frequents() {
            const ids = Object.keys(this.favoris.internes)
            if (!ids.length) return []
            const parId = {}
            this.medicaments.forEach(m => { parId[m.id] = m })
            return ids
                .map(id => ({ medicament: parId[id], count: this.favoris.internes[id] }))
                .filter(e => e.medicament)
                .sort((a, b) => b.count - a.count)
                .slice(0, 8)
                .map(e => e.medicament)
        },

        /**
         * Médicaments hors stock les plus prescrits par ce médecin (top 4).
         */
        externes_frequents() {
            return Object.values(this.favoris.externes)
                .sort((a, b) => b.count - a.count)
                .slice(0, 4)
        },
    },

    mounted() {
        this.getListeMedicaments()
        this.patient_id = this.$route.params.id
        this.loadFavoris()
        this.loadPosos()
        this.loadModeles()
        this.loadRepresc()

        this.$store.dispatch('getDifferentialComputing')
    },

    methods: {
        getListeMedicaments() {
            this.$store.dispatch("getMedicaments")
        },

        filterMedicament() {
            this.filtered_medicaments = [];
            if (this.value.length > 2) {
                this.medicaments.forEach(
                    element => {
                        if (element.libelle.toLowerCase().includes(this.value.toLowerCase())) {
                            this.filtered_medicaments.push(element)
                        }
                    }
                )
            }
        },

        checkPrescriptionQuantite(prescription) {
            if (prescription.interne === true) {
                return prescription.quantite_stock
            } else {
                return 100000000000000000
            }

        },

        addInterneMedicament(index, medicament) {

            this.prescriptions[index].medicament_stock = medicament.id
            this.prescriptions[index].medicament_libelle = medicament.libelle
            this.prescriptions[index].prix = medicament.prix
            this.prescriptions[index].interne = true
            this.prescriptions[index].quantite_stock = medicament.qte_disponible

            // Le conditionnement connu du stock (lot_article, ex. "B/10") est repris
            // automatiquement ; le médecin peut toujours le modifier.
            if (!this.prescriptions[index].conditionnement) {
                this.prescriptions[index].conditionnement = medicament.lot_article || ''
            }


            this.value = ""
            this.filtered_medicaments = []

            this.appliquerPosoMemorisee(index)

        },

        addExterneMedicament(index, libelle) {

            this.prescriptions[index].medicament_libelle = libelle || this.value
            this.prescriptions[index].interne = false

            this.value = ""
            this.filtered_medicaments = []

            this.appliquerPosoMemorisee(index)

        },

        // ------- Favoris médecin (mémorisés localement, par utilisateur) -------

        favKey() {
            return 'ordo_fav_' + this.user_id
        },

        loadFavoris() {
            let favoris = null
            try {
                favoris = JSON.parse(localStorage.getItem(this.favKey()))
            } catch (e) { /* stockage indisponible : favoris ignorés */ }
            if (!favoris || typeof favoris !== 'object') favoris = {}
            this.favoris = {
                internes: favoris.internes && typeof favoris.internes === 'object' ? favoris.internes : {},
                externes: favoris.externes && typeof favoris.externes === 'object' ? favoris.externes : {}
            }
        },

        /**
         * Après chaque ordonnance enregistrée, compte les médicaments prescrits
         * (internes par id stock, externes par libellé) pour les proposer en
         * badges « Fréquents » à la prochaine ouverture.
         */
        enregistrerFavoris() {
            this.prescriptions.forEach(prescription => {
                if (!prescription.medicament_libelle) return
                if (prescription.interne === true && prescription.medicament_stock) {
                    const id = prescription.medicament_stock
                    this.favoris.internes[id] = (this.favoris.internes[id] || 0) + 1
                } else {
                    const cle = prescription.medicament_libelle.trim().toLowerCase()
                    if (!cle) return
                    const existant = this.favoris.externes[cle] || { libelle: prescription.medicament_libelle.trim(), count: 0 }
                    existant.count++
                    this.favoris.externes[cle] = existant
                }
            })
            try {
                localStorage.setItem(this.favKey(), JSON.stringify(this.favoris))
            } catch (e) { /* quota dépassé : favoris ignorés */ }
        },

        // ------- Posologies mémorisées (par médecin et par médicament) -------

        posoKey() {
            return 'ordo_poso_' + this.user_id
        },

        loadPosos() {
            let posos = null
            try {
                posos = JSON.parse(localStorage.getItem(this.posoKey()))
            } catch (e) { /* stockage indisponible : posologies ignorées */ }
            if (!posos || typeof posos !== 'object') posos = {}
            this.posos = {
                internes: posos.internes && typeof posos.internes === 'object' ? posos.internes : {},
                externes: posos.externes && typeof posos.externes === 'object' ? posos.externes : {}
            }
        },

        /**
         * Après chaque ordonnance enregistrée, mémorise pour chaque médicament
         * la dernière quantité / posologie / conditionnement utilisés, ainsi que
         * l'état de l'assistant (_poso) pour le ré-afficher tel quel.
         */
        enregistrerPosos() {
            this.prescriptions.forEach(prescription => {
                if (!prescription.medicament_libelle) return
                const memo = {
                    quantite: prescription.quantite,
                    conditionnement: prescription.conditionnement,
                    posologie: prescription.posologie,
                    _poso: { ...prescription._poso }
                }
                if (prescription.interne === true && prescription.medicament_stock) {
                    this.posos.internes[prescription.medicament_stock] = memo
                } else {
                    const cle = prescription.medicament_libelle.trim().toLowerCase()
                    if (!cle) return
                    this.posos.externes[cle] = memo
                }
            })
            try {
                localStorage.setItem(this.posoKey(), JSON.stringify(this.posos))
            } catch (e) { /* quota dépassé : posologies ignorées */ }
        },

        /**
         * Si ce médecin a déjà prescrit ce médicament, pré-remplit quantité,
         * conditionnement et posologie avec ses habitudes (modifiables).
         */
        appliquerPosoMemorisee(index) {
            const prescription = this.prescriptions[index]
            let memo = null
            if (prescription.interne === true && prescription.medicament_stock) {
                memo = this.posos.internes[prescription.medicament_stock]
            } else if (prescription.medicament_libelle) {
                memo = this.posos.externes[prescription.medicament_libelle.trim().toLowerCase()]
            }
            if (!memo) return
            if (memo.quantite) prescription.quantite = memo.quantite
            if (memo.conditionnement) prescription.conditionnement = memo.conditionnement
            if (memo.posologie) prescription.posologie = memo.posologie
            if (memo._poso && typeof memo._poso === 'object') {
                prescription._poso = { ...posoVide(), ...memo._poso }
            }
        },

        // ------- Ordonnances types (modèles réutilisables, par médecin) -------

        modeleKey() {
            return 'ordo_modeles_' + this.user_id
        },

        loadModeles() {
            let modeles = null
            try {
                modeles = JSON.parse(localStorage.getItem(this.modeleKey()))
            } catch (e) { /* stockage indisponible : modèles ignorés */ }
            this.modeles = Array.isArray(modeles) ? modeles : []
        },

        persistModeles() {
            try {
                localStorage.setItem(this.modeleKey(), JSON.stringify(this.modeles))
            } catch (e) { /* quota dépassé : modèles ignorés */ }
        },

        /**
         * Au moins une ligne complète (médicament + posologie) est nécessaire
         * pour que le modèle soit réutilisable tel quel.
         */
        peutSauvegarderModele() {
            return this.prescriptions.some(p => p.medicament_libelle && p.posologie)
        },

        /**
         * Enregistre les lignes remplies sous un nom (« Paludisme simple »…).
         */
        sauvegarderModele() {
            const nom = this.nom_modele.trim()
            if (!nom || !this.peutSauvegarderModele()) return
            const lignes = this.prescriptions
                .filter(p => p.medicament_libelle)
                .map(p => ({
                    medicament_stock: p.medicament_stock,
                    medicament_libelle: p.medicament_libelle,
                    interne: p.interne,
                    quantite: p.quantite,
                    posologie: p.posologie,
                    conditionnement: p.conditionnement,
                    prix: p.prix,
                    _poso: { ...p._poso }
                }))
            const existant = this.modeles.findIndex(m => m.nom === nom)
            if (existant >= 0) {
                this.modeles.splice(existant, 1, { nom, lignes })
            } else {
                this.modeles.push({ nom, lignes })
            }
            this.persistModeles()
            this.nom_modele = ''
            this.modele_naming = false
        },

        /**
         * Applique un modèle : remplit toutes les lignes d'un coup. Pour les
         * médicaments du stock, le prix et la quantité disponible sont relus
         * dans le catalogue actuel (le médicament a pu changer depuis).
         */
        appliquerModele(modele) {
            const lignes = modele.lignes.map(l => {
                const ligne = { ...lignePrescriptionVide(), ...l, _poso: { ...posoVide(), ...(l._poso || {}) } }
                if (ligne.interne === true && ligne.medicament_stock) {
                    const actuel = this.medicaments.find(m => m.id === ligne.medicament_stock)
                    if (actuel) {
                        ligne.medicament_libelle = actuel.libelle
                        ligne.prix = actuel.prix
                        ligne.quantite_stock = actuel.qte_disponible
                    } else {
                        // Médicament retiré du stock : on le prescrit en externe.
                        ligne.interne = false
                        ligne.medicament_stock = null
                        ligne.quantite_stock = null
                    }
                }
                return ligne
            })
            this.prescriptions = lignes.length ? lignes : [lignePrescriptionVide()]
            this.value = ''
            this.filtered_medicaments = []
        },

        supprimerModele(index) {
            this.modeles.splice(index, 1)
            this.persistModeles()
        },

        // ------- Re-prescription depuis l'historique (panier local) -------

        represcKey() {
            return 'ordo_represc_' + this.user_id
        },

        loadRepresc() {
            let panier = null
            try {
                panier = JSON.parse(localStorage.getItem(this.represcKey()))
            } catch (e) { /* stockage indisponible */ }
            this.represc = (panier && Array.isArray(panier.lignes) && panier.lignes.length) ? panier : null
        },

        /**
         * Applique l'ordonnance copiée depuis l'historique (même logique de
         * relecture du stock qu'un modèle), puis vide le panier.
         */
        appliquerRepresc() {
            if (!this.represc) return
            this.appliquerModele({ lignes: this.represc.lignes })
            this.ignorerRepresc()
        },

        ignorerRepresc() {
            this.represc = null
            try {
                localStorage.removeItem(this.represcKey())
            } catch (e) { /* stockage indisponible */ }
        },

        /**
         * Clic sur une ligne de la « Liste Médicaments » : ajoute le médicament
         * à la première ligne de prescription vide (ou en crée une nouvelle),
         * puis referme la liste.
         */
        addFromListe(medicament) {
            let index = this.prescriptions.findIndex(p => p.medicament_libelle === '')
            if (index === -1) {
                this.addPrescription()
                index = this.prescriptions.length - 1
            }
            this.addInterneMedicament(index, medicament)
            if (this.$refs.modalMedicament) {
                this.$refs.modalMedicament.click()
            }
        },


        addPrescription() {
            this.prescriptions.push(lignePrescriptionVide())
        },

        removePrescription(index) {
            this.prescriptions.splice(index, 1)

        },

        /**
         * Unité de prise déduite de la forme présente dans le libellé
         * (« Paracétamol 500mg comprimé » → « comprimé »).
         */
        unitePosologie(prescription) {
            const libelle = (prescription.medicament_libelle || '').toLowerCase()
            if (libelle.includes('comprim')) return 'comprimé'
            if (libelle.includes('gélule') || libelle.includes('gelule')) return 'gélule'
            if (libelle.includes('inject')) return 'injection'
            if (libelle.includes('sirop') || libelle.includes('suspension')) return 'dose'
            if (libelle.includes('sachet')) return 'sachet'
            if (libelle.includes('suppositoire')) return 'suppositoire'
            if (libelle.includes('goutte')) return 'goutte'
            if (libelle.includes('pulvéris') || libelle.includes('spray')) return 'pulvérisation'
            return 'unité'
        },

        /**
         * Clic sur un chip de fréquence (1×/2×/3× par jour) : mode « jour », génère la posologie.
         */
        setPosoFreq(index, freq) {
            const poso = this.prescriptions[index]._poso
            poso.mode = 'jour'
            poso.freq = freq
            this.buildPosologie(index)
        },

        /**
         * Clic sur le chip « Chaque… » : mode « chaque N heures / jours, à partir de…, pendant… »,
         * génère la posologie.
         */
        setPosoChaque(index) {
            const poso = this.prescriptions[index]._poso
            poso.mode = 'chaque'
            poso.freq = 0
            this.buildPosologie(index)
        },

        /**
         * Dose et durée nettoyées (entiers ≥ 1) pour le texte généré.
         */
        posoNombres(poso) {
            let dose = parseInt(poso.dose, 10)
            if (!(dose > 0)) dose = 1
            let duree = parseInt(poso.duree, 10)
            if (!(duree > 0)) duree = 1
            return { dose: dose, duree: duree }
        },

        /**
         * Complément « à partir de … » du mode « chaque » : aujourd'hui, demain ou une date (JJ/MM/AAAA).
         */
        texteDebutPoso(poso) {
            if (poso.debut === 'demain') return ' à partir de demain'
            if (poso.debut === 'date') {
                const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(poso.debut_date || '')
                return m ? ' à partir du ' + m[3] + '/' + m[2] + '/' + m[1] : ''
            }
            return " à partir d'aujourd'hui"
        },

        /**
         * Texte du mode « chaque » : « 1 comprimé toutes les 6 heures à partir d'aujourd'hui pendant 3 jours »,
         * « 2 gélules tous les 3 jours à partir du 30/09/2026 pendant 15 jours ».
         */
        textePosologieChaque(prescription) {
            const poso = prescription._poso
            const nombres = this.posoNombres(poso)
            let chaque = parseInt(poso.chaque, 10)
            if (!(chaque > 0)) chaque = 1
            let frequence
            if (poso.unite_chaque === 'jours') {
                frequence = chaque > 1 ? 'tous les ' + chaque + ' jours' : 'tous les jours'
            } else {
                frequence = chaque > 1 ? 'toutes les ' + chaque + ' heures' : 'toutes les heures'
            }
            const unite = this.unitePosologie(prescription)
            return nombres.dose + ' ' + unite + (nombres.dose > 1 ? 's' : '')
                + ' ' + frequence + this.texteDebutPoso(poso)
                + ' pendant ' + nombres.duree + ' jour' + (nombres.duree > 1 ? 's' : '')
        },

        /**
         * Génère le texte de posologie.
         * Mode « jour » : dose × fréquence (1×/2×/3× par jour) × durée.
         * Mode « chaque » : dose toutes les N heures / tous les N jours, à partir de…, pendant la durée.
         * N'écrase le texte libre qu'une fois l'assistant utilisé (un chip cliqué) ;
         * le champ reste modifiable après génération.
         */
        buildPosologie(index) {
            const prescription = this.prescriptions[index]
            const poso = prescription._poso
            if (!poso) return
            if (poso.mode === 'chaque') {
                prescription.posologie = this.textePosologieChaque(prescription)
                return
            }
            if (!poso.freq) return

            const moments = {
                1: 'le matin',
                2: 'le matin et le soir',
                3: 'le matin, à midi et le soir'
            }[poso.freq]
            if (!moments) return

            const nombres = this.posoNombres(poso)
            const unite = this.unitePosologie(prescription)
            prescription.posologie = nombres.dose + ' ' + unite + (nombres.dose > 1 ? 's' : '')
                + ' ' + moments + ' pendant ' + nombres.duree + ' jour' + (nombres.duree > 1 ? 's' : '')
        },

        clearError() {
            this.permission_is_denied = false
            this.is_error = false
            this.is_paid = false
            $('.err-msg').fadeOut()
        },

        async submitOrdonnance() {
            this.clearError()
            const isValid = await this.$refs.observer.validate();

            if (!isValid) {
                return
            }

            let liste_prescriptions = []
            this.prescriptions.forEach(prescription => {
                const tmp_prescription = {
                    id: prescription.medicament_stock,
                    nom: prescription.medicament_libelle,
                    interne: prescription.interne,
                    qte: prescription.quantite,
                    posologie: prescription.posologie,
                    conditionnement: prescription.conditionnement,
                    prix: prescription.prix,
                }
                liste_prescriptions.push(tmp_prescription)
            });

            liste_prescriptions = this.setPrestationUID(liste_prescriptions)

            const data = {
                consultation_id: this.consultation_id,
                evolution_id: this.evolution_id,
                ordonnance_id: this.selected_ordonnance.ordonnance_id,
                prescriptions: liste_prescriptions,
                user_id: this.user_id,
                patient_id: this.patient_id,
                type_prestation: 'Médicaments',
                can_set_pac: this.can_set_pac,
                info_patient: this.info_patient,
                info_caissier: this.info_caissier
            }

            this.save_loading = true
            this.$store.dispatch('saveConsultationOrdonnance', data).then((response) => {
                if (response.success) {
                    this.$refs.modal_ordonnance.click()
                    this.$emit("emitRefreshOrdonnance")

                    this.enregistrerFavoris()
                    this.enregistrerPosos()
                    this.prescriptions = [lignePrescriptionVide()]

                    const op_data = {
                        ...response.data,
                        duree_forfait_hospi_est_expire: this.info_assurance_patient.duree_forfait_hospi_est_expire
                    }
                    store_finance.storeLigneOperation(op_data).then((finance_response) => {
                        if (!finance_response.success) {
                            this.errorToast(finance_response.message)
                            return;
                        } 
                    }).catch(() => {
                        this.errorToast("Une erreur s'est produite lors de l'enregistrement à la caisse")
                    })
                } else {
                    if (response.code === 500) {
                        this.is_error = true
                    } else if (response === 403) {
                        this.permission_is_denied = true
                    }
                }
                this.save_loading = false
            }).catch(() => {
                this.save_loading = false
                this.is_error = true
            })
        },

        /**
         * Cache le message d'erreur au click du checkbox
         */
        onConfirm() {
            $('.err-msg').fadeOut(500);
        },

        confirmer() {
            this.is_confirm = true
        },

        /**
		 * Retirer un examen de la liste de demande
		 */
        deletePrescription(selector) {
            const confirm = document.getElementById(selector);

            if (!confirm.checked) { //  Vérifie si l'utilisateur a confirmé la suppression
                $('.err-msg').fadeIn(500)
                return
            }

            this.del_loading = true
            
            this.$store.dispatch('removePrescription', this.data_to_be_deleted).then((response) => {
                if (response.success) {
                    this.successToast('Suppression effectuée avec succès')
                    this.$emit("emitRefreshOrdonnance")

                    this.$refs.del_prescription.click()

                    store_finance.removeItemFromCaisse(response.data).then((finance_response) => {
                        if (!finance_response.success) {
                            this.errorToast(finance_response.message)
                            return;
                        } 
                    }).catch(() => {
                        this.errorToast("Une erreur s'est produite lors de l'enregistrement à la caisse")
                    })
                } else {
                    if (response.code == 403) {     // Can't delete due to the fact that it's already paid
                        this.error_message = "Impossible de supprimer !"
                    } else if (response.code == 409) {      // Permission denied
                        this.error_message = "Impossible de supprimer ! Ce médicament a été déjà payé"
                    } else if (response.code == 500) {      // An error occured
                        this.error_message = "Une erreur s'est produite !"
                    }
                }
                
                this.del_loading = false;
                this.is_confirm = false
            }).catch(() => {
                this.del_loading = false
                this.error_message = "Une erreur s'est produite !"
            })
        },

        deleteOrdonnance(selector) {
            let confirm = document.getElementById(selector);
            
            if (confirm.checked == false) {     //  Vérifie si l'utilisateur a confirmé la suppression
                $('.err-msg').fadeIn(500)
                this.del_loading = false
                return
            }
            
            this.del_loading = true;
            this.$store.dispatch('deleteOrdonnance', this.data_to_be_deleted).then((response) => {
                if (response.success) {
                    this.successToast("Suppression effectuée avec succès")
                    this.$emit("emitRefreshOrdonnance")

                    this.is_confirm = false
                    this.$refs.del_ordonnance.click();

                    store_finance.removeAllItemsFromCaisse(response.data).then((finance_response) => {
                        if (!finance_response.success) {
                            this.errorToast(finance_response.message)
                            return;
                        } 
                    }).catch(() => {
                        this.errorToast("Une erreur s'est produite lors de l'enregistrement à la caisse")
                    })
                } else {
                    if (response.code == 403) {
                        this.permission_is_denied = true
                    } else if (response.code == 409) {
                        this.is_conflict = true
                    } 
                }
                this.del_loading = false
            }).catch(() => {
                this.del_loading = false
                this.is_error = true
            })
        },

        async savePrescription() {
            this.clearError()
            const isValid = await this.$refs.observer.validate();

            if (!isValid) {
                return
            }
            this.selected_prescription.can_set_pac = this.can_set_pac
            this.selected_prescription.can_add_differential_computing = this.can_add_differential_computing.response
            this.save_loading = true
            this.$store.dispatch('updatePrescription', this.selected_prescription).then((response) => {
                if (response.success) {
                    this.$refs.edit_ordonnance.click()
                    this.$emit("emitRefreshOrdonnance")

                    this.prescriptions = [lignePrescriptionVide()]

                    store_finance.updateLigneOpOrdonnance(response.data).then((finance_response) => {
                        if (!finance_response.success) {
                            this.errorToast(finance_response.message)
                            return;
                        } 
                    }).catch(() => {
                        this.errorToast("Une erreur s'est produite lors de l'enregistrement à la caisse")
                    })
                } else {
                    this.error_message = response.message
                }
                this.save_loading = false
            }).catch(() => {
                this.save_loading = false
                this.error_message = "Une erreur s'est produite lors de la modification de la prescription !"
            })
        }
    }
}